import { Component, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { FormControl } from '@angular/forms';
import {
	takeUntil,
	debounceTime,
	distinctUntilChanged,
	delay,
	filter,
} from 'rxjs/operators';
import { MatSort } from '@angular/material/sort';
import { animateList } from 'src/shared/animations';
import { UserListArgs } from 'src/shared/api';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';

import * as CustomerUserSelectors from '../+state/customer-users.selectors';
import * as CustomerUserActions from '../+state/customer-users.actions';
import { CustomerUsersPartialState } from '../+state/customer-users.reducer';
import { Store, select } from '@ngrx/store';
import { FilterCollection } from 'src/admin-app/utilities/list-filtering/shared';
import {
	withAbstractControl,
	withMatPaginator,
	withMatSort,
} from 'src/admin-app/utilities/list-filtering';
import { customersFragments } from 'src/admin-app/customers/customers.routes';
import { usersFragments } from '../../users.routes';

@Component({
	templateUrl: './customer-user-list.component.html',
	animations: [animateList],
})
export class CustomerUserListComponent extends SelfDestruct
	implements AfterViewInit, OnDestroy {
	@ViewChild(MatSort, { static: true }) sort: MatSort;
	@ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

	readonly emptyColumns = ['empty'];
	readonly resultColumns = ['name', 'email', 'phoneNumber', 'companyName'];
	readonly customersFragments = customersFragments;
	readonly usersFragments = usersFragments;

	readonly filter = new FormControl('') as FormControl;

	readonly isLoading$ = this.store.pipe(
		select(CustomerUserSelectors.getListLoadingStatus)
	);
	readonly listMessage$ = this.store.pipe(
		select(CustomerUserSelectors.getListMessage)
	);

	readonly pagedCustomerUsers$ = this.store.pipe(
		select(CustomerUserSelectors.getFilteredAndPagedCustomerUsers)
	);

	readonly pagedCustomerUsersCount$ = this.store.pipe(
		select(CustomerUserSelectors.getPagedCustomerUsersCount)
	);

	constructor(private readonly store: Store<CustomerUsersPartialState>) {
		super();
	}

	ngAfterViewInit() {
		const filters = new FilterCollection<UserListArgs>(
			withAbstractControl(this.filter),
			withMatPaginator(this.paginator),
			withMatSort(this.sort)
		);

		filters.valueChanges
			.pipe(
				debounceTime(200),
				distinctUntilChanged(),
				takeUntil(this.destroyed)
			)
			.subscribe((v) =>
				this.store.dispatch(
					CustomerUserActions.filtersUpdated({ filters: v })
				)
			);

		// Initialize filters with URL value
		this.store
			.pipe(
				select(CustomerUserSelectors.getListArgs),
				delay(0),
				filter<UserListArgs>(Boolean),
				takeUntil(this.destroyed)
			)
			.subscribe((f) => filters.setValue(f));
	}

	ngOnDestroy() {
		this.store.dispatch(CustomerUserActions.customerUsersListDestroyed());
		super.ngOnDestroy();
	}
}
